/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, FormLabel, Stack, Tooltip, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

export const FieldFormControl = ({
	field,
	label,
	error: propError,
	errorMessage: propErrorMessage,
	currentValue: propCurrentValue,
	disabled,
	labelStyle,
	children,
}) => {
	const theme = useTheme();
	const labelRef = useRef(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const { getFieldMeta, values } = useFormikContext();
	const { isProspect } = useClientSchemaContext();
	const { field: fieldConfig } = field;

	const { error: fieldErrorMessage, touched } = getFieldMeta(field.name);

	const currentValue = propCurrentValue ?? values[field.name] ?? '';
	const errorMessage = propErrorMessage ?? fieldErrorMessage;
	const error = touched && (Boolean(errorMessage) || propError);

	const required = field.mandatory[isProspect ? 'prospect' : 'client'];

	const helperText = field.helperText;

	useLayoutEffect(() => {
		if (labelRef.current) {
			const el = labelRef.current;
			setIsTruncated(el.scrollWidth > el.clientWidth);
		}
	}, [label, field.label]);

	const labelNode = (
		<FormLabel
			ref={labelRef}
			htmlFor={field.name}
			sx={{
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				display: 'block',
				maxWidth: '100%',
				...(labelStyle === 'subtle' && {
					color: theme.palette.text.secondary,
					fontWeight: 400,
					fontSize: '0.75rem',
				}),
			}}
		>
			{label ?? field.label}
		</FormLabel>
	);

	return (
		<FormControl fullWidth {...{ error, required, disabled }}>
			<Stack direction='row' justifyContent='space-between' alignItems='center'>
				<Tooltip title={isTruncated ? (label ?? field.label) : ''}>
					<span style={{ width: '100%' }}>{labelNode}</span>
				</Tooltip>

				{helperText && (
					<Tooltip title={helperText}>
						<InfoOutlineIcon fontSize='1.25rem' sx={{ color: theme.palette.text.secondary }} />
					</Tooltip>
				)}
			</Stack>

			{children}
			<Stack
				direction='row'
				justifyContent={error ? 'space-between' : 'flex-end'}
				width='100%'
			>
				{error && <FormHelperText error>{errorMessage}</FormHelperText>}

				{fieldConfig.max && (
					<FormHelperText
						error={error || currentValue.length > fieldConfig.max}
						sx={{
							...(currentValue.length >= fieldConfig.max && {
								fontWeight: 'bold',
							}),
							mr: '14px',
							fontSize: '0.7rem',
							color: theme.palette.text.secondary,
						}}
					>
						<span>{currentValue.length}</span>
						{`/${fieldConfig.max}`}
					</FormHelperText>
				)}
			</Stack>
		</FormControl>
	);
};
export default FieldFormControl;
